/**
 * Bloco de suporte técnico da aba Contatos (GUPM-200) — diretório por empresa.
 * Os dados chegam por props (`useContatosDaAba`, C-75); aqui é só apresentação.
 *
 * Layout fiel ao V8 (`_moutContatos`): nome (negrito) + cargo abaixo, dividers
 * entre contatos, escopo no rodapé. Empresas na ordem de `SUPORTE_TECNICO`.
 * Contatos cujo `empresa` não casa a estrutura caem num bucket "Outras empresas"
 * (nunca somem silenciosamente — achado do self-review) + `console.warn`.
 */
import { SUPORTE_TECNICO, empresasVisiveis } from '@/domain/outros';
import type { BlocoContatos } from '@/hooks/useContatosDaAba';
import type { ContatoGestao } from '@/services/datasources';
import LinhaContato from './LinhaContato';

interface CardEmpresaProps {
  empresa: string;
  papel: string;
  cor: string;
  escopo: string;
  contatos: ContatoGestao[];
  /** Só mostra o vazio "a cadastrar" quando NÃO está carregando nem em erro. */
  quieto: boolean;
  onEditar?: (contato: ContatoGestao) => void;
}

function CardEmpresa({ empresa, papel, cor, escopo, contatos, quieto, onEditar }: CardEmpresaProps) {
  return (
    <div
      className="rounded-lg border border-jf-border bg-jf-surface p-3 border-l-[3px]"
      style={{ borderLeftColor: cor }}
    >
      <div className="flex items-center gap-2.5">
        <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: cor }} aria-hidden />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-white">{empresa}</h3>
          <p className="text-[10px] text-jf-muted">{papel}</p>
        </div>
      </div>

      {contatos.length > 0 ? (
        <ul className="mt-2">
          {contatos.map((c) => (
            <LinhaContato key={c.id} contato={c} onEditar={onEditar} seloEmergencia />
          ))}
        </ul>
      ) : (
        quieto && (
          <p className="mt-2 border-t border-jf-border pt-2 text-[11px] italic text-jf-dim">
            Contatos a cadastrar.
          </p>
        )
      )}

      {escopo && (
        <p className="mt-2 border-t border-dashed border-jf-border pt-2 text-[11px] leading-relaxed text-jf-dim">
          {escopo}
        </p>
      )}
    </div>
  );
}

interface Props {
  bloco: BlocoContatos;
  onEditar?: (contato: ContatoGestao) => void;
}

export default function ContatosSuporteView({ bloco, onEditar }: Props) {
  const { contatos, isLoading, isError } = bloco;

  // Agrupa por empresa; a ordem de exibição vem de SUPORTE_TECNICO (estrutura V8).
  const porEmpresa = new Map<string, ContatoGestao[]>();
  for (const c of contatos) {
    const empresa = c.empresa ?? '';
    porEmpresa.set(empresa, [...(porEmpresa.get(empresa) ?? []), c]);
  }

  // Contatos cujo `empresa` não está na estrutura conhecida — não podem sumir.
  const conhecidas = new Set(SUPORTE_TECNICO.map((e) => e.empresa));
  const orfas = [...porEmpresa.entries()].filter(([empresa]) => !conhecidas.has(empresa));
  if (orfas.length > 0) {
    const total = orfas.reduce((n, [, lista]) => n + lista.length, 0);
    console.warn(
      `[Contatos de Suporte] ${total} contato(s) com empresa fora da estrutura padrão:`,
      orfas.map(([empresa]) => empresa),
    );
  }

  const quieto = !isLoading && !isError;

  return (
    <section aria-labelledby="contatos-suporte-titulo" className="space-y-3">
      <div>
        <h2 id="contatos-suporte-titulo" className="text-sm font-bold text-white">
          🛠️ Suporte técnico
        </h2>
        <p className="text-xs text-jf-muted">
          Suporte técnico (não-emergencial) por empresa — estrutura de escalonamento.
        </p>
      </div>

      {isLoading && <p className="text-xs text-jf-dim">Carregando contatos…</p>}
      {isError && (
        <p className="text-xs text-red-400">Não foi possível carregar os contatos. Tente novamente.</p>
      )}

      {empresasVisiveis(contatos).map((e) => (
        <CardEmpresa
          key={e.empresa}
          empresa={e.empresa}
          papel={e.papel}
          cor={e.cor}
          escopo={e.escopo}
          contatos={porEmpresa.get(e.empresa) ?? []}
          quieto={quieto}
          onEditar={onEditar}
        />
      ))}

      {orfas.map(([empresa, lista]) => (
        <CardEmpresa
          key={empresa}
          empresa={empresa || 'Sem empresa'}
          papel="Empresa fora da estrutura padrão — revisar cadastro"
          cor="#6B7280"
          escopo=""
          contatos={lista}
          quieto={quieto}
          onEditar={onEditar}
        />
      ))}
    </section>
  );
}
